import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /**
   * In-flight Section Rendering API fetches keyed by page number, so the
   * eager loader and the sentinel-triggered prefetch never fetch the same
   * page twice concurrently.
   * @type {Map<number, Promise<void>>}
   */
  #inFlightPageFetches = new Map();

  /**
   * In-flight eager load — re-entrant callers join the same promise so
   * loading-state UI tied to completion can't be cleared early.
   * @type {Promise<void> | null}
   */
  #eagerLoadPromise = null;

  /**
   * Bumped whenever the page cache is invalidated (filter update) so an
   * in-progress eager load loop knows to stop appending stale pages.
   * @type {number}
   */
  #pagesGeneration = 0;

  /**
   * Serializes ALL page renders (both directions). The sentinel
   * IntersectionObserver and the eager loader can otherwise be in flight
   * for the same page simultaneously — each parses the cached page HTML
   * into FRESH elements (#getGridForPage), so concurrent renders append
   * the same products twice. Every render is queued and recomputes its
   * target page from the live DOM when it actually runs, so a queued
   * duplicate naturally resolves to the next page or a no-op.
   * @type {Promise<void>}
   */
  #renderChain = Promise.resolve();

  /**
   * @param {() => Promise<void>} task
   * @returns {Promise<void>}
   */
  #enqueueRender(task) {
    const run = this.#renderChain.then(() => task());
    // Keep the chain alive even if a render rejects.
    this.#renderChain = run.catch(() => {});
    return run;
  }

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } finally {
      // Always unblock waiting renders — a rejected fetch must not leave
      // #renderNextPage / #renderPreviousPage (or the eager loader) hung.
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   * @returns {Promise<void>}
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    if (this.pages.has(pageNumber)) return;

    const inFlight = this.#inFlightPageFetches.get(pageNumber);
    if (inFlight) return inFlight;

    const generation = this.#pagesGeneration;
    const fetchPromise = sectionRenderer
      .getSectionHTML(this.sectionId, true, pageInfo.url)
      .then((pageContent) => {
        // Drop the result if the cache was invalidated while in flight —
        // the content belongs to the previous filter state.
        if (generation !== this.#pagesGeneration) return;
        this.pages.set(pageNumber, pageContent);
      })
      .catch((error) => {
        // Treat a failed fetch as "no content": waiting renders resolve and
        // find nothing, the eager loader stops on no-progress, and the
        // sentinel observers retry on the next scroll intersection.
        console.warn(`PaginatedList: failed to fetch page ${pageNumber}`, error);
      })
      .finally(() => {
        this.#inFlightPageFetches.delete(pageNumber);
      });

    this.#inFlightPageFetches.set(pageNumber, fetchPromise);
    return fetchPromise;
  }

  /**
   * Note: appends NEVER write `?page=N` to history. The OOTB Horizon
   * behaviour pushed the page param on every sentinel append, which made
   * reloads land mid-list (triggering the eager backfill's scroll-anchored
   * prepends — a visibly strange load) and polluted shared/bookmarked URLs.
   * The URL stays wherever the shopper actually navigated to.
   */
  #renderNextPage() {
    return this.#enqueueRender(() => this.#renderNextPageNow());
  }

  async #renderNextPageNow() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();
    this.#notifyPageContentAppended();

    // Re-observe the sentinel so the IntersectionObserver delivers a fresh
    // entry for its current state. Without this, client-side hiding (e.g.
    // the COBS bakery filter) leaves the sentinel inside the viewport and
    // the chain stalls after a single page because IntersectionObserver
    // only fires on state transitions.
    this.#repokeInfinityScrollObserver();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  #notifyPageContentAppended() {
    document.dispatchEvent(new CustomEvent(ThemeEvents.PageContentAppended, { bubbles: true }));
  }

  #repokeInfinityScrollObserver() {
    if (!this.infinityScrollObserver) return;
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (viewMorePrevious) {
      this.infinityScrollObserver.unobserve(viewMorePrevious);
      this.infinityScrollObserver.observe(viewMorePrevious);
    }
    if (viewMoreNext) {
      this.infinityScrollObserver.unobserve(viewMoreNext);
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /** See #renderNextPage — prepends never write `?page=N` to history. */
  #renderPreviousPage() {
    return this.#enqueueRender(() => this.#renderPreviousPageNow());
  }

  async #renderPreviousPageNow() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Prepend the new elements. NO scroll compensation — the collection
    // grid must never issue programmatic scrolls (the OOTB scrollTo
    // anchoring was removed along with native scroll anchoring via
    // `overflow-anchor: none`); backfill prepends normally happen behind
    // the eager-loading veil where a content shift is invisible anyway.
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();
    this.#notifyPageContentAppended();

    this.#repokeInfinityScrollObserver();

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Eagerly loads every remaining page of the list into the grid (in both
   * directions, so `?page=N` deep links backfill to page 1 first — the
   * backfill prepends happen behind the eager-loading veil, so the
   * resulting content shift is invisible). Appends strictly in page order
   * via the same render paths as the sentinel observers — every append
   * fires PageContentAppended so availability hydration / sorting /
   * filters run via their existing listeners.
   *
   * No-ops when the list fits on one page, exceeds `maxPages`, or an eager
   * load is already in flight. Degrades gracefully: any page that fails to
   * fetch stops the loop and the sentinel observers carry on as today.
   *
   * @param {{ maxPages?: number }} [options] - `maxPages` caps the TOTAL
   *   page count this will eagerly load (not the number of fetches).
   * @returns {Promise<void>} Resolves when eager loading settles. Re-entrant
   *   callers join the in-flight promise.
   */
  loadAllRemainingPages({ maxPages = Infinity } = {}) {
    if (this.#eagerLoadPromise) return this.#eagerLoadPromise;

    const { grid } = this.refs;
    if (!grid) return Promise.resolve();

    const lastPage = Number(grid.dataset.lastPage);
    if (!Number.isFinite(lastPage) || lastPage <= 1 || lastPage > maxPages) {
      return Promise.resolve();
    }

    this.#eagerLoadPromise = (async () => {
      try {
        // Pre-warm every remaining page's fetch in parallel FIRST so the
        // network requests overlap instead of running one-at-a-time behind
        // the loading veil. #fetchSpecificPage caches into `this.pages` and
        // dedupes via `#inFlightPageFetches`, so the serial render loops
        // below then just consume cached HTML (a pure DOM append, no
        // per-page network round-trip) — cutting the post-200 reveal delay
        // from N sequential fetches to roughly one fetch's worth.
        this.#prewarmRemainingPages(lastPage);

        // Backfill toward page 1 first so a shopper landing on ?page=3 gets
        // the full set; scroll anchoring in #renderPreviousPage keeps their
        // viewport stable while pages prepend above.
        await this.#eagerLoadDirection('previous');
        await this.#eagerLoadDirection('next');
      } finally {
        this.#eagerLoadPromise = null;
      }
    })();

    return this.#eagerLoadPromise;
  }

  /**
   * Fires a fetch for every not-yet-loaded page (in both directions, from the
   * current loaded bounds out to page 1 / `lastPage`) WITHOUT awaiting them,
   * so all the page HTML downloads concurrently. Idempotent and safe to call
   * repeatedly: #fetchSpecificPage short-circuits pages already cached or
   * in-flight. The eager render loops await the per-page render (which reads
   * the cache these fetches populate), so this only parallelises the network,
   * never the DOM writes.
   *
   * @param {number} lastPage
   */
  #prewarmRemainingPages(lastPage) {
    const lowest = this.#loadedPageBound('previous');
    const highest = this.#loadedPageBound('next');

    if (Number.isFinite(lowest)) {
      for (let p = /** @type {number} */ (lowest) - 1; p >= 1; p--) {
        void this.#fetchSpecificPage(p);
      }
    }
    if (Number.isFinite(highest)) {
      for (let p = /** @type {number} */ (highest) + 1; p <= lastPage; p++) {
        void this.#fetchSpecificPage(p);
      }
    }
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #eagerLoadDirection(type) {
    const generation = this.#pagesGeneration;
    const lastPage = Number(this.refs.grid?.dataset.lastPage) || 0;

    // Bounded by the total page count so a bug can never loop forever.
    for (let i = 0; i < lastPage; i++) {
      if (generation !== this.#pagesGeneration) return; // filters changed mid-load
      if (!this.isConnected) return;

      const pageInfo = this.#getPage(type);
      if (!pageInfo || !this.#shouldUsePage(pageInfo)) return;

      const marker = this.#loadedPageBound(type);
      if (type === 'previous') {
        await this.#renderPreviousPage();
      } else {
        await this.#renderNextPage();
      }

      // No progress means the fetch failed or returned nothing — stop and
      // let the sentinel observers retry on scroll as they do today.
      if (this.#loadedPageBound(type) === marker) return;

      // Yield between appends so hydration / sort listeners and paint can
      // run before the next page lands.
      await new Promise((resolve) => requestIdleCallback(() => resolve(undefined)));
    }
  }

  /**
   * The lowest (previous direction) / highest (next direction) `data-page`
   * across ALL cards in the grid — deliberately NOT the positional
   * first/last card. availability-grid-sort.js reorders the grid globally,
   * so after a re-sort the card at either DOM edge can belong to ANY loaded
   * page (e.g. an unavailable page-2 product sorted to the bottom). Deriving
   * the next page from the positional edge card re-appended already-loaded
   * pages, rendering every product on them twice.
   *
   * @param {"previous" | "next"} type
   * @returns {number | undefined}
   */
  #loadedPageBound(type) {
    const { cards } = this.refs;
    if (!Array.isArray(cards)) return undefined;

    let bound;
    for (const card of cards) {
      const page = Number(card?.dataset.page);
      if (!Number.isFinite(page)) continue;
      if (bound === undefined || (type === 'previous' ? page < bound : page > bound)) {
        bound = page;
      }
    }
    return bound;
  }

  /**
   * Hook for subclasses: called after a filter update has replaced the grid
   * and the page cache has been invalidated (new `data-last-page` observed).
   * Base implementation is a no-op.
   */
  onPagesInvalidated() {}

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === 'previous';

    // Min/max data-page across all cards, NOT the positional edge card —
    // see #loadedPageBound for why (global availability re-sort).
    const currentCardPage = this.#loadedPageBound(type);

    if (currentCardPage === undefined) return;

    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#inFlightPageFetches.clear();
    this.#pagesGeneration += 1;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');

        // Let subclasses re-trigger eager loading against the new result set
        this.onPagesInvalidated();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
